<script lang="ts">
    import type { Price, SaleTags } from '@shared/types/types';

    interface Props {
        price: Price;
        saleTags?: SaleTags;
        dealHighlight?: string;
        variationId: string;
        rule?: string;
        showInstallments?: boolean;
    }

    let { price, saleTags, dealHighlight, variationId, rule, showInstallments = false }: Props = $props();

    const displayPrice = $derived(`${price.isStartingPrice ? 'ab ' : ''} ${price.retailPrice} €`);
    const benefitUrl = $derived(
        saleTags?.benefit?.id &&
            rule &&
            `/benefit-sheet/sheet/list/${saleTags.benefit.id}?contextDreson=${rule}&variationId=${variationId}&useOcSheet=true`,
    );

    const installmentPrice = $derived.by(() => {
        if (!price.installmentPlan?.amount) {
            return;
        }
        return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
            price.installmentPlan.amount,
        );
    });
</script>

<div class="reptile-price">
    <div class="reptile-price__priceAndUVP">
        <span
            class={[
                'oc-headline-50',
                'reptile-price__retailPrice',
                'reptile-price__priceValue',
                price.sale && 'reptile-price__priceValue--red',
            ]}
        >
            {displayPrice}
        </span>
        {#if price.sale}
            {#if price.suggestedRetailPrice}
                <span class="oc-copy-75"
                    >UVP <span class={['reptile-price__priceValue', 'reptile-price__priceValue--strikethrough']}
                        >{price.suggestedRetailPrice} €</span
                    >
                </span>
            {:else if price.comparativePrice}
                <span class={['reptile-price__priceValue', 'reptile-price__priceValue--strikethrough', 'oc-copy-75']}>
                    {price.comparativePrice} €
                </span>
            {/if}
        {/if}
    </div>
    {#if dealHighlight}
        <span class={['reptile-price__priceHighlightText', 'oc-headline-50']}>{dealHighlight}</span>
    {/if}
    {#if price.normPrice}
        <span class="oc-copy-75">
            ({price.normPrice.value} €/ {price.normPrice.amount}
            {price.normPrice.unit})
        </span>
    {/if}
    {#if showInstallments && price.installmentPlan}
        <span class="oc-copy-75" data-testid="reptile-installments"
            ><span class="oc-copy--bold">{installmentPrice}</span> mtl. in {price.installmentPlan.count}
            Raten</span
        >
    {/if}
    {#if saleTags}
        <div class="reptile-price__saleTags">
            {#if saleTags?.discount}
                <oc-tag-v1 variant="sale" size={'50'}>{saleTags.discount}</oc-tag-v1>
            {/if}
            {#if saleTags?.benefit?.value && benefitUrl}
                <oc-tag-v1
                    variant="sale"
                    size={'50'}
                    show-info-icon
                    data-oc-sheet-v1-create
                    data-oc-sheet-v1-create.base64-url={btoa(benefitUrl)}>{saleTags.benefit.value}</oc-tag-v1
                >
            {/if}
        </div>
    {/if}
</div>

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens' as tokens;
    .reptile-price {
        display: flex;
        gap: tokens.$oc-semantic-spacing-12;
        flex-direction: column;
        align-items: baseline;
        text-align: left;
        width: 100%;

        &__priceAndUVP {
            align-items: baseline;
            display: flex;
            flex-flow: row wrap-reverse;
        }

        &__retailPrice {
            margin-right: tokens.$oc-semantic-spacing-25;
        }

        &__priceValue {
            white-space: nowrap;

            &--strikethrough {
                text-decoration: line-through;
            }

            &--red {
                color: tokens.$oc-semantic-color-text-sale;
            }
        }

        &__priceHighlightText {
            white-space: nowrap;
            color: tokens.$oc-base-color-otto-red-100;
        }

        &__saleTags {
            display: flex;
            flex-flow: row wrap;
            align-items: center;
            gap: tokens.$oc-semantic-spacing-50;
            width: 100%;
            margin: tokens.$oc-semantic-spacing-25 0 0;
        }
    }
</style>
